'use client';

import React from 'react';
import { useInView } from 'react-intersection-observer';
import { SessionContext } from '@benzinga/session-context';
import type { AdManager, AdPartner, BaseAdSlotEvent } from '@benzinga/ads-utils';
import { trackAdImpression } from '../utils/trackAdImpression';

export interface useAdImpressionConfig<T extends BaseAdSlotEvent> {
  containerId: string | null;
  manager: AdManager<T>;
  partnerId: AdPartner;
  unitType: string;
}

export interface useAdImpressionResult<T extends BaseAdSlotEvent> {
  containerRef: (node?: Element | null) => void;
  didImpress: boolean;
  isAdLoaded: boolean;
  slotEvent: T | null;
}

export function useAdImpression<T extends BaseAdSlotEvent>({
  containerId,
  manager,
  partnerId,
  unitType,
}: useAdImpressionConfig<T>): useAdImpressionResult<T> {
  const session = React.useContext(SessionContext);
  const [slotEvent, setSlotEvent] = React.useState<T | null>(null);
  const [didImpress, setDidImpress] = React.useState(false);

  const { inView, ref: inViewRef } = useInView({ triggerOnce: true });

  React.useEffect(() => {
    if (!containerId) return;

    const cleanup = manager.observeAdLoad(containerId, event => {
      setSlotEvent(event);
    });

    return cleanup;
  }, [containerId, manager]);

  const isAdLoaded = slotEvent !== null && !slotEvent.isEmpty;
  const destinationUrl = slotEvent?.destinationUrl ?? null;

  React.useEffect(() => {
    if (!containerId) return;
    if (inView && isAdLoaded && !didImpress) {
      setDidImpress(true);

      trackAdImpression({
        destinationUrl,
        partnerId,
        session,
        unitType,
      });
    }
  }, [inView, isAdLoaded, didImpress, destinationUrl, partnerId, unitType, session, containerId]);

  return {
    containerRef: inViewRef,
    didImpress,
    isAdLoaded,
    slotEvent,
  };
}
